import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { Client } from '@/components/clients/types';
import { EnrichedData } from '@/components/clients/enrichment-dialog/enrichmentTypes';
import { useEnrichmentDialogManager, EnrichmentDialogDataType } from './enrichment/useEnrichmentDialogManager';
import { useEnrichmentDataManager } from './enrichment/useEnrichmentDataManager';
import { useEnrichmentPollingController } from './enrichment/useEnrichmentPollingController';
import { useEnrichmentTrigger } from './enrichment/useEnrichmentTrigger';
import { EnrichmentResponse } from '@/hooks/useCompanyEnrichment';
import { supabase } from '@/integrations/supabase/client';

interface UseClientEnrichmentHandlerProps {
  client: Client | undefined | null;
  refetchClient: () => void;
}

// Helper to map data from polling (client_enrichment_results schema) to Partial<EnrichmentResponse>
const mapPollingDataToEnrichmentResponse = (pollingData: any): Partial<EnrichmentResponse> => {
  if (!pollingData) return {};
  return {
    lutra_run_id: pollingData.run_id,
    progress_stage: pollingData.progress_stage,
    error_message: pollingData.error_message,
    status: pollingData.status,
    result_data: pollingData.enriched_data ? (pollingData.enriched_data as EnrichedData) : null,
    // message could be derived if needed
    // debug could be added if pollingData contains it
  };
};

export const useClientEnrichmentHandler = ({ client, refetchClient }: UseClientEnrichmentHandlerProps) => {
  const {
    isEnrichmentDialogOpen,
    enrichmentDialogData,
    openEnrichmentDialog,
    closeEnrichmentDialog,
  } = useEnrichmentDialogManager();

  const {
    lastRunId,
    setLastRunId,
    lastEnrichmentData,
    setLastEnrichmentData,
    fetchEnrichmentDataByRunId,
  } = useEnrichmentDataManager({ client });

  const { triggerNewEnrichmentFlow, isEnrichmentTriggering } = useEnrichmentTrigger({
    setLastEnrichmentData,
    setLastRunId,
    refetchClient,
    lastRunIdToMaintain: lastRunId,
  });

  const [isCancellingEnrichment, setIsCancellingEnrichment] = useState(false);

  const handlePollingUpdate = useCallback((data: any) => { // data is from client_enrichment_results
    console.log('Polling update received. Refetching client data.', data);
    // Optionally, map data and setLastEnrichmentData if intermediate updates are needed for UI
    // For now, just refetching client is the primary action.
    // If needed: setLastEnrichmentData(mapPollingDataToEnrichmentResponse(data));
    refetchClient();
  }, [refetchClient]);

  const handlePollingComplete = useCallback((data: any) => { // data is from client_enrichment_results
    setLastEnrichmentData(mapPollingDataToEnrichmentResponse(data));
    toast.success(`${client?.name || 'Client'} enrichment complete!`, {
      description: "Data is available for review."
    });
    refetchClient();
  }, [client?.name, refetchClient, setLastEnrichmentData]);

  const handlePollingError = useCallback((errorMessage: string, errorDetails?: any, rawData?: any) => { // rawData from client_enrichment_results
    setLastEnrichmentData((prev) => {
      const base = prev || {};
      const mappedRaw = rawData ? mapPollingDataToEnrichmentResponse(rawData) : {};
      
      return {
        ...base,
        ...mappedRaw,
        error_message: errorMessage,
        progress_stage: base?.progress_stage?.startsWith('error_') 
                          ? base.progress_stage 
                          : (errorDetails?.stage || errorDetails?.rawResponse?.progress_stage || rawData?.progress_stage || 'error_unknown'),
        status: 'error', // Explicitly set status to error
        debug: errorDetails, // Assuming errorDetails can be debug info
      };
    });
    toast.error(`Enrichment error for ${client?.name || 'Client'}: ${errorMessage}`);
    refetchClient();
  }, [client?.name, refetchClient, setLastEnrichmentData]);

  const { isPolling } = useEnrichmentPollingController({
    client,
    runId: lastRunId,
    lastEnrichmentData,
    onUpdate: handlePollingUpdate,
    onComplete: handlePollingComplete,
    onError: handlePollingError,
  });

  const triggerEnrichment = useCallback(async () => { // Renamed from original `triggerEnrichment` in prompt, assuming this is the intended `triggerNewEnrichmentFlow` for client
    if (!client) return;
    await triggerNewEnrichmentFlow(client);
  }, [client, triggerNewEnrichmentFlow]);

  const handleEnrichClient = useCallback(async () => {
    if (!client) return;

    // --- Helper function to determine the current enrichment action ---
    const determineEnrichmentAction = (): 'FETCH_EXISTING' | 'SHOW_IN_PROGRESS' | 'TRIGGER_NEW' => {
      if (client.enrichment_status === 'available' && client.last_enrichment_run_id) {
        return 'FETCH_EXISTING';
      }
      if (
        client.enrichment_status === 'pending' ||
        client.enrichment_status === 'processing' ||
        isEnrichmentTriggering ||
        isPolling ||
        (lastEnrichmentData &&
          (lastEnrichmentData.progress_stage?.includes('pending') ||
            lastEnrichmentData.progress_stage?.includes('polling') ||
            lastEnrichmentData.progress_stage === 'requested' ||
            lastEnrichmentData.progress_stage === 'initiated_lutra') &&
          !lastEnrichmentData.progress_stage?.startsWith('error_') &&
          lastEnrichmentData.status !== 'completed' &&
          lastEnrichmentData.status !== 'applied')
      ) {
        return 'SHOW_IN_PROGRESS';
      }
      return 'TRIGGER_NEW';
    };

    // --- Helper function to handle fetching existing enrichment data ---
    const handleFetchExistingAction = async () => {
      if (!client.last_enrichment_run_id) return; // Should be guaranteed by determineEnrichmentAction

      toast.info(`Fetching latest enrichment results for ${client.name}...`);
      const existingEnrichment = await fetchEnrichmentDataByRunId(client.id, client.last_enrichment_run_id);

      if (!existingEnrichment || existingEnrichment.error_message) {
        toast.error(`Could not fetch existing data: ${existingEnrichment?.error_message || 'Not found'}. Triggering new enrichment.`);
        await triggerNewEnrichmentFlow(client);
        return;
      }
      
      const dialogPayload: EnrichmentDialogDataType = {
        client,
        result: existingEnrichment as EnrichmentResponse,
      };
      openEnrichmentDialog(dialogPayload);
    };

    // --- Helper function to handle showing in-progress enrichment dialog ---
    const handleShowInProgressAction = async () => {
      let currentDialogData: Partial<EnrichmentResponse> | null = lastEnrichmentData;

      if (client.last_enrichment_run_id && client.last_enrichment_run_id !== lastRunId) {
        setLastRunId(client.last_enrichment_run_id);
        // fetchEnrichmentDataByRunId updates lastEnrichmentData internally via setLastEnrichmentData
        const fetchedData = await fetchEnrichmentDataByRunId(client.id, client.last_enrichment_run_id);
        currentDialogData = fetchedData; // Use the most recent data
      }
      
      const finalDataForDialog: Partial<EnrichmentResponse> = currentDialogData || {
        progress_stage: client.enrichment_status || 'pending',
        status: client.enrichment_status || 'pending',
        lutra_run_id: client.last_enrichment_run_id || lastRunId,
      };
                                           
      const stageMessage = finalDataForDialog?.progress_stage ? `(Current stage: ${finalDataForDialog.progress_stage})` : "";
      toast.info(`Enrichment for ${client.name} is in progress. ${stageMessage}`, {
        description: "The status banner and dialog will reflect updates."
      });

      const dialogPayload: EnrichmentDialogDataType = {
          client,
          result: finalDataForDialog as EnrichmentResponse,
      };
      openEnrichmentDialog(dialogPayload);
    };

    // --- Determine and execute the action ---
    const action = determineEnrichmentAction();

    switch (action) {
      case 'FETCH_EXISTING':
        await handleFetchExistingAction();
        break;
      case 'SHOW_IN_PROGRESS':
        await handleShowInProgressAction();
        break;
      case 'TRIGGER_NEW':
        await triggerNewEnrichmentFlow(client);
        break;
    }
  }, [
    client, 
    lastEnrichmentData, 
    isPolling,
    isEnrichmentTriggering,
    fetchEnrichmentDataByRunId, 
    triggerNewEnrichmentFlow, 
    openEnrichmentDialog, 
    lastRunId, 
    setLastRunId,
    // toast is stable, no need to list as dep typically
  ]);

  const handleEnrichmentDialogClose = useCallback(() => {
    closeEnrichmentDialog();
  }, [closeEnrichmentDialog]);

  const handleEnrichmentApplied = useCallback(async () => {
    refetchClient(); 
    closeEnrichmentDialog();
    if (client?.id && lastRunId) {
      // fetchEnrichmentDataByRunId already sets lastEnrichmentData
      await fetchEnrichmentDataByRunId(client.id, lastRunId); 
    }
    toast.success("Client data updated with enriched information.");
  }, [refetchClient, closeEnrichmentDialog, client?.id, lastRunId, fetchEnrichmentDataByRunId]);

  const handleCancelEnrichment = useCallback(async (runIdToCancel: string, clientIdToCancel: string) => {
    if (!runIdToCancel || !clientIdToCancel) {
      toast.error("Missing run ID or client ID for cancellation.");
      return;
    }
    setIsCancellingEnrichment(true);
    toast.info(`Attempting to cancel enrichment for ${client?.name || 'client'}...`);

    try {
      const { error } = await supabase.functions.invoke('cancel-enrichment', {
        body: { run_id: runIdToCancel, client_id: clientIdToCancel },
      });

      if (error) {
        console.error("Error cancelling enrichment:", error);
        throw new Error(error.message || "Failed to cancel enrichment via function.");
      }
      
      toast.success(`Enrichment for ${client?.name || 'client'} has been requested to cancel.`);
      refetchClient();
      closeEnrichmentDialog(); // Close dialog after successful cancellation request
      // Update local state if needed, but refetchClient should handle it
      setLastEnrichmentData(prev => ({
        ...prev,
        status: 'cancelled',
        progress_stage: 'cancelled',
        lutra_run_id: runIdToCancel
      }));

    } catch (err: any) {
      console.error("handleCancelEnrichment error:", err);
      toast.error(`Failed to cancel enrichment: ${err.message || 'Unknown error'}`);
    } finally {
      setIsCancellingEnrichment(false);
    }
  }, [client?.name, refetchClient, closeEnrichmentDialog, setLastEnrichmentData]);

  return {
    handleEnrichClient,
    isEnrichmentTriggering,
    isEnrichmentPolling: isPolling,
    isEnrichmentDialogOpen,
    enrichmentDialogData, // This data is EnrichmentDialogDataType { client, result: EnrichmentResponse | null }
    lastEnrichmentData,   // This is Partial<EnrichmentResponse> | null
    handleEnrichmentDialogClose,
    handleEnrichmentApplied,
    triggerNewEnrichment: triggerEnrichment,
    handleCancelEnrichment,
    isCancellingEnrichment,
  };
};
